/**
 * A contract's client signing (R191, decision 1010 item 2; decision 854: docuseal.com; docs/specs/portal/
 * client-approvals.md §5): "Send to the client to sign" keeps the full contract with DocuSeal's tags on the client's
 * lines; a client admin opens it in the client portal and signs it in DocuSeal; Teslyn keeps the signed copy and the
 * audit log. Staff then record the signing as before ("Record signing", filled in from DocuSeal), which makes the
 * contract the signed one and its changes variations.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { localIsoDate, signoffDate } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { SelectField } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import {
  contractSigningUrl,
  loadContractApprovals,
  loadContractSigners,
  refreshContractSigning,
  sendContractApproval,
  signedContractUrl,
  withdrawApproval,
  type ContractSigner,
  type StaffApproval,
} from './api.ts';
import { ApprovalLine, SendForApproval } from './ApprovalParts.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const defaultOpen = (url: string): void => {
  const tab = window.open(url, '_blank', 'noopener');
  if (!tab) window.location.assign(url);
};

/** The date of a DocuSeal signing in Perth's day (YYYY-MM-DD), for "Record signing". */
function signedDay(iso: string): string {
  const [d, m, y] = signoffDate(iso).split('/');
  return d && m && y ? `${y}-${m}-${d}` : localIsoDate();
}

export function ContractApprovalPanel({
  sb,
  tenantId,
  userId,
  contract,
  write,
  makePdf,
  onRecordSigning,
  open = defaultOpen,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  /** The person signed in: the manager a contract names signs it here once the client has (decision 1014). */
  readonly userId: string;
  readonly contract: { readonly id: string; readonly number: string; readonly status: string };
  /** Tenant admins and managers (contract.write) send and withdraw. */
  readonly write: boolean;
  /** The full contract as the client signs it (DocuSeal's tags on the client's lines). */
  readonly makePdf: () => Promise<{ readonly pdf: Uint8Array; readonly fileName: string }>;
  /** Open "Record signing" filled in from DocuSeal's signing. */
  readonly onRecordSigning: (date: string, fileName: string) => void;
  readonly open?: (url: string) => void;
}): ReactElement {
  const [rows, setRows] = useState<StaffApproval[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  // decision 1014: the manager who signs after the client, the sending manager by default
  const [signers, setSigners] = useState<ContractSigner[]>([]);
  const [manager, setManager] = useState('');
  const reload = useCallback(() => {
    setError(null);
    loadContractApprovals(sb, contract.id)
      .then(setRows)
      .catch((e: unknown) => setError(message(e)));
  }, [sb, contract.id]);
  useEffect(reload, [reload]);
  useEffect(() => {
    if (!write) return;
    let live = true;
    loadContractSigners(sb, tenantId)
      .then((s) => {
        if (!live) return;
        setSigners(s);
        setManager((m) => m || (s.find((x) => x.user_id === userId) ?? s[0])?.user_id || '');
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [sb, tenantId, userId, write]);

  async function run(key: string, what: () => Promise<string | null>): Promise<void> {
    setBusy(key);
    setNotice(null);
    try {
      const ok = await what();
      if (ok) setNotice({ ok: true, text: ok });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
    }
  }

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!rows) return <Loading what="the client signing" />;
  const waiting = rows.find((r) => r.state === 'waiting') ?? null;
  const signed = rows.find((r) => r.state === 'approved') ?? null;
  const canSend = write && contract.status === 'issued';
  return (
    <section className="panel stack" aria-label="Client signing">
      <h2 className="heading">Client signing (DocuSeal)</h2>
      <p className="body muted">
        Send the full contract to the client&apos;s admin to sign in the client portal with DocuSeal. Your
        organisation&apos;s DocuSeal account is used (Integrations); the client never sees its key. Teslyn
        keeps the signed copy and DocuSeal&apos;s audit log.
      </p>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {rows.length === 0 && <p className="body muted">Not sent to the client to sign.</p>}
      <ul className="stack">
        {rows.map((r) => (
          <li key={r.id} className="stack approval-item">
            <ApprovalLine row={r} />
            {r.signing_state === 'failed' && r.signing_message && (
              <p className="caption">DocuSeal: {r.signing_message}</p>
            )}
            <div className="row row--wrap">
              {r.state === 'waiting' &&
                r.manager_user_id === userId &&
                r.client_signed_at &&
                !r.manager_signed_at && (
                  <Button
                    variant="primary"
                    busy={busy === `sign:${r.id}`}
                    onClick={() =>
                      void run(`sign:${r.id}`, async () => {
                        open(await contractSigningUrl(sb, r.id));
                        return 'DocuSeal opened in a new tab. Once you have signed, the contract shows as signed by both.';
                      })
                    }
                  >
                    Sign with DocuSeal
                  </Button>
                )}
              {r.state === 'waiting' && (r.signing_state === 'sent' || r.signing_state === 'sending') && (
                <Button
                  variant="quiet"
                  busy={busy === `check:${r.id}`}
                  onClick={() =>
                    void run(`check:${r.id}`, async () => {
                      await refreshContractSigning(sb, r.id);
                      return null;
                    })
                  }
                >
                  Check DocuSeal now
                </Button>
              )}
              {r.signed_available && (
                <Button
                  variant="quiet"
                  busy={busy === `signed:${r.id}`}
                  onClick={() =>
                    void run(`signed:${r.id}`, async () => {
                      open(await signedContractUrl(sb, { id: r.id, tenant_id: tenantId, title: r.title }));
                      return null;
                    })
                  }
                >
                  Download the signed copy
                </Button>
              )}
              {r.state === 'waiting' && write && (
                <Button
                  variant="quiet"
                  busy={busy === `withdraw:${r.id}`}
                  onClick={() =>
                    void run(`withdraw:${r.id}`, async () => {
                      await withdrawApproval(sb, r);
                      return `${r.title} was withdrawn from the client.`;
                    })
                  }
                >
                  Withdraw
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {signed && write && contract.status !== 'signed' && contract.status !== 'closed' && (
        <div className="row row--wrap">
          <p className="body grow">
            The client signed on {signoffDate(signed.decided_at ?? signed.sent_at)}. Record the signing to
            make this the signed contract.
          </p>
          <Button
            variant="primary"
            onClick={() =>
              onRecordSigning(
                signedDay(signed.decided_at ?? signed.sent_at),
                `${signed.title.replace(/\s+/g, '_')}_Signed.pdf`,
              )
            }
          >
            Record signing
          </Button>
        </div>
      )}
      {canSend && (
        <SelectField<string>
          label="Signs for the operator after the client"
          value={manager}
          onChange={setManager}
          options={
            signers.length
              ? signers.map((s) => [s.user_id, s.name] as const)
              : [['', 'No manager with an email']]
          }
          hint="DocuSeal asks this manager once the client has signed, by email and in Teslyn Cloud."
        />
      )}
      {canSend && (
        <SendForApproval
          label={waiting ? 'Send a new copy to sign' : 'Send to the client to sign'}
          what={`contract ${contract.number}`}
          busy={busy === 'send'}
          onSend={(note) =>
            void run('send', async () => {
              const { pdf, fileName } = await makePdf();
              await sendContractApproval(sb, {
                tenantId,
                contractId: contract.id,
                pdf,
                fileName,
                note,
                managerUserId: manager || null,
              });
              const who = signers.find((s) => s.user_id === manager)?.name ?? 'the manager';
              return `Contract ${contract.number} was sent to the client to sign. Their client admin signs it from the client portal, then ${who} signs for the operator.`;
            })
          }
        />
      )}
      {write && contract.status === 'draft' && (
        <p className="caption muted">
          Mark the contract as issued first, then send it to the client to sign.
        </p>
      )}
    </section>
  );
}
